"use client";

import React, { useState, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Image from "next/image";

import { bitrixLeadAction } from "../actions/bitrix";
import { zohoLeadAction } from "../actions/zoho";
import { staticFormsActions } from "../actions/staticForms";
import { trackEvent } from "../lib/engageXClient";
import { leadEmailAction } from "../actions/email";
import { leadFields } from "../emailTemplates/leadTemplate";
import { isPhoneValid } from "../../src/lib/validatePhone";
import { useDictionary } from "../../src/context/dictionaryProvider";

import InputTextField from "../../src/components/atoms/InputTextField";
import PhoneField from "../../src/components/atoms/PhoneField";

import GoldBadge from "../../public/lpa_gold.png";

/* VALIDATION */
const formSchema = yup.object({
  name: yup
    .string()
    .required("Name is required")
    .min(3)
    .max(25)
    .matches(/^[a-zA-Z0-9\s]+$/, "No special characters allowed"),
  email: yup.string().email().required(),
  phone: yup
    .string()
    .required()
    .test("isPhone", "Invalid phone", (v) => isPhoneValid(v)),
});

const ContactModal = ({ course }) => {
  const searchParams = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [countryName, setCountryName] = useState("");

  const exitBlocked = useRef(false); // true after form submit OR manual close
  const popupOpen = useRef(false);   // prevents double trigger
  const dict = useDictionary()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({ resolver: yupResolver(formSchema) });



  /* ---------------------------- CLOSE HANDLERS ---------------------------- */

  // 🔥 Manual Close (Block all future popups)
  const closePopup = () => {
    exitBlocked.current = true;   // ⬅️ REQUIRED FIX
    popupOpen.current = false;

    setIsOpen(false);
    setShowThankYou(false);
    reset();
  };

  // Silent auto-close after submit
  const silentClose = () => {
    popupOpen.current = false;
    setIsOpen(false);
    setShowThankYou(false);
    reset();
  };

  /* ---------------------------- SUBMIT HANDLER ---------------------------- */
  const onSubmit = async (data) => {
    const dataWithCountry = {
      ...data,
      country_name: countryName || ""
    };
    try {
      await staticFormsActions("ContactModal", data);
      await bitrixLeadAction({ ...data, course }, searchParams, 50);
      const utm = {
        utm_source: localStorage.getItem("utm_source") || "",
        utm_medium: localStorage.getItem("utm_medium") || "",
        utm_campaign: localStorage.getItem("utm_campaign") || "",
        utm_term: localStorage.getItem("utm_term") || "",
        utm_content: localStorage.getItem("utm_content") || "",
        click_id: localStorage.getItem("click_id") || "",
      };

      await zohoLeadAction({ ...dataWithCountry, ...utm, course }, window.location.href, 50, "Website Contact Info Form");

      trackEvent("enquiry_submitted", { form: "exit_popup", course: course?.name });

      await leadEmailAction("Exit Popup - Exit Intent", leadFields(data));

      exitBlocked.current = true;  // Don't open again after submit
      popupOpen.current = false;

      setShowThankYou(true);

      setTimeout(() => {
        silentClose();
      }, 2500);
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    }
  };

  if (!isOpen) return null;

  /* ---------------------------- MODAL UI ---------------------------- */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full z-10 overflow-hidden">
        <div className="grid md:grid-cols-[45%_55%]">
          <div className="relative min-h-[550px]">
            {!isSubmitting && (
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 text-white z-20 bg-black/20 rounded-full p-2 hover:bg-black/30 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            <Image
              src={GoldBadge}
              alt="Gold CMA Badge"
              fill
              className="object-cover"
              priority
            />
          </div>

          <div className="p-8 md:p-10 flex flex-col justify-center bg-white">
            {showThankYou ? (
              <div className="text-center py-8">
                <div className="mb-4">
                  <svg className="w-16 h-16 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
                <p className="text-gray-600">We will contact you shortly.</p>
              </div>
            ) : isSubmitting ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Submitting your request...</p>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  GRAB your FREE IMA USA Membership & CMA Entrance NOW
                </h2>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <InputTextField
                    label="Name"
                    placeholder="Your name"
                    isInvalid={!!errors?.name}
                    errorMessage={errors?.name?.message}
                    {...register("name")}
                  />

                  <InputTextField
                    label="Email"
                    type="email"
                    placeholder="your@email.com"
                    isInvalid={!!errors?.email}
                    errorMessage={errors?.email?.message}
                    {...register("email")}
                  />

                  <PhoneField
                    label={dict?.form.fieldNames.phone}
                    name="phone"
                    value={getValues("phone")}
                    onChange={(value, metadata) => {
                      setValue("phone", value, { shouldDirty: true, shouldValidate: isDirty });
                      //  Capture country name
                      if (metadata?.country?.name) {
                        setCountryName(metadata.country.name);
                      }
                    }}
                    isInvalid={!!errors?.phone}
                    errorMessage={errors?.phone?.message}
                  />

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 bg-[#0F1B62] text-white rounded-lg hover:bg-[#1E3A8A] transition-all font-semibold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
